import { AddOutlined } from '@mui/icons-material'
import {
    DataGrid,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { FabLink } from '@/components/FabLink'
import { businessFieldNameAndSortByEnumMap } from '@/features/admin/businesses/overview/helper/businessFieldNameAndSortByEnumMap'
import { businessQueryFilter } from '@/features/admin/businesses/overview/helper/businessQueryFilter'
import { useDeleteBusiness } from '@/features/admin/businesses/overview/mutations/useDeleteBusiness'
import { AdminOverviewPagePendingComponent } from '@/features/admin/components/AdminOverviewPagePendingComponent'
import { CustomAdminDataGridToolbar } from '@/features/admin/components/CustomAdminDataGridToolbar'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { BusinessSortBy } from '@/generated/Enumerations/BusinessSortBy_pb'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { BusinessDto } from '@/generated/Messages/Basic_pb'
import { UuidDto } from '@/generated/Messages/Common_pb'
import { debounce } from '@/helper/debounce'
import { stringifyId } from '@/helper/id'
import { useCreateCUDColumn } from '@/hooks/useCreateCUDColumn'
import { businessesQueryOptions, useBusinesses } from '@/queries/useBusinesses'
import {
    searchParamsDeletedItemSelectionDefaultValues,
    searchParamsDeletedItemSelectionSchema,
} from '@/schemas/searchParams/searchParamsDeletedItemSelectionSchema'
import {
    availablePageSizes,
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import {
    searchParamsSortingDefaultValues,
    searchParamsSortingSchema,
} from '@/schemas/searchParams/searchParamsSortingSchema'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'businesses'

const useStaticColumns = (): GridColDef<BusinessDto>[] => {
    const { t } = useTranslation()

    return React.useMemo(
        () => [
            {
                field: 'name',
                headerName: t('business.name'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'description',
                headerName: t('common.description'),
                minWidth: 200,
                flex: 1,
                filterable: false,
                hideable: false,
                resizable: false,
                sortable: false,
            },

            {
                field: 'deletedAt',
                headerName: t('common.active'),
                flex: 0.5,
                minWidth: 150,
                valueGetter: (_, row) => !row.timestamps?.deletedAt,
                sortable: false,
                hideable: false,
                resizable: false,

                type: 'boolean',
                filterable: true,
            },
        ],
        [t]
    )
}

const RouteComponent: React.FC = () => {
    const {
        deletedItemSelection,
        page,
        pageSize,
        sortByField,
        sortDirectionDesc,
        searchText,
    } = Route.useSearch()
    const navigate = Route.useNavigate()
    const { t } = useTranslation()

    const {
        data: { businesses, pagination },
    } = useBusinesses(
        businessQueryFilter(
            deletedItemSelection,
            pageSize,
            page,
            sortByField,
            sortDirectionDesc,
            searchText
        )
    )

    const deleteBusiness = useDeleteBusiness()

    const [businessToDelete, setBusinessToDelete] = React.useState<UuidDto>()

    const staticColumns = useStaticColumns()

    const cudColumn = useCreateCUDColumn<BusinessDto>({
        viewLinkProps: row => ({
            to: '/admin/businesses/$id/view',
            params: { id: stringifyId(row.id) },
        }),
        editLinkProps: row => ({
            to: '/admin/businesses/$id/edit',
            params: {
                id: stringifyId(row.id),
            },
            disabled: !!row.timestamps?.deletedAt,
        }),
        deleteProps: row => ({
            onClick: () => setBusinessToDelete(row.id),
            disabled: !!row.timestamps?.deletedAt,
        }),
    })

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeSearchText = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText, page: 0 }),
            })
        }),
        []
    )

    const paginationModel = React.useMemo(
        (): GridPaginationModel => ({ page, pageSize }),
        [page, pageSize]
    )
    const sortModel = React.useMemo(
        (): GridSortModel =>
            (
                sortByField !== BusinessSortBy.UNSPECIFIED &&
                businessFieldNameAndSortByEnumMap[sortByField]
            ) ?
                [
                    {
                        field: businessFieldNameAndSortByEnumMap[sortByField],
                        sort: sortDirectionDesc ? 'desc' : 'asc',
                    },
                ]
            :   [],
        [sortByField, sortDirectionDesc]
    )
    const filterModel = React.useMemo(
        () =>
            deletedItemSelection !== DeletedItemSelection.BOTH ?
                {
                    items: [
                        {
                            field: 'deletedAt',
                            operator: 'is',
                            value:
                                deletedItemSelection ===
                                DeletedItemSelection.NORMAL_ONLY,
                        },
                    ],
                }
            :   undefined,
        [deletedItemSelection]
    )

    return (
        <div>
            <title>{t('business.urlIndex')}</title>

            <DataGrid
                rows={businesses}
                columns={columns}
                pageSizeOptions={availablePageSizes}
                disableRowSelectionOnClick={true}
                getRowId={({ name }) => name}
                slots={{ toolbar: CustomAdminDataGridToolbar }}
                slotProps={{
                    toolbar: {
                        createButton: (
                            <FabLink
                                variant={'extended'}
                                color={'primary'}
                                disabled={false}
                                to={'/admin/businesses/create'}
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                                data-selenium={`${seleniumPrefix}-new`}
                            >
                                <AddOutlined />
                                {t('common.new')}
                            </FabLink>
                        ),
                        initialSearchText: searchText,
                        onChangeSearchText,
                    },
                }}
                paginationMode={'server'}
                filterMode={'server'}
                sortingMode={'server'}
                rowCount={pagination?.totalItemCount ?? 0}
                paginationModel={paginationModel}
                sortModel={sortModel}
                filterModel={filterModel}
                onPaginationModelChange={async model =>
                    await navigate({ search: prev => ({ ...prev, ...model }) })
                }
                onFilterModelChange={async model => {
                    const deletedItemSelection = model.items.find(
                        i => i.field === 'deletedAt'
                    )?.value as boolean | undefined

                    await navigate({
                        search: prev => ({
                            ...prev,

                            page: 0,

                            deletedItemSelection:
                                deletedItemSelection === undefined ?
                                    DeletedItemSelection.BOTH
                                : deletedItemSelection ?
                                    DeletedItemSelection.NORMAL_ONLY
                                :   DeletedItemSelection.DELETED_ONLY,
                        }),
                    })
                }}
                onSortModelChange={async ([model]) => {
                    const castField = model?.field as
                        | keyof OmitDeepMessageFields<BusinessDto>
                        | undefined
                    const field =
                        castField ?
                            businessFieldNameAndSortByEnumMap[castField]
                        :   undefined

                    await navigate({
                        search: prev => ({
                            ...prev,
                            page: 0,
                            sortByField: field,
                            sortDirectionDesc: model?.sort === 'desc',
                        }),
                    })
                }}
                showToolbar={true}
            />
            <FabLink
                variant={'extended'}
                color={'primary'}
                disabled={false}
                to={'/admin/businesses/create'}
                sx={theme => ({
                    display: { xs: 'inline-flex', sm: 'none' },
                    position: 'absolute',
                    bottom: theme.spacing(2),
                    right: theme.spacing(2),
                })}
                data-selenium={`${seleniumPrefix}-new`}
            >
                <AddOutlined />
                {t('common.new')}
            </FabLink>

            <ConfirmationDialog
                open={!!businessToDelete}
                onClickNo={() => setBusinessToDelete(undefined)}
                onClickYes={async () => {
                    if (!businessToDelete) {
                        return
                    }

                    setBusinessToDelete(undefined)
                    await deleteBusiness.mutateAsync(businessToDelete)
                }}
            />
        </div>
    )
}

const PendingComponent: React.FC = () => {
    const { page, pageSize, searchText } = Route.useSearch()
    const staticColumns = useStaticColumns()
    const cudColumn = useCreateCUDColumn<BusinessDto>()
    const { t } = useTranslation()

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    return (
        <AdminOverviewPagePendingComponent
            pageTitle={t('business.urlIndex')}
            columns={columns}
            page={page}
            pageSize={pageSize}
            searchText={searchText}
        />
    )
}

export const Route = createFileRoute('/__base-layout/admin/businesses/')({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsDeletedItemSelectionSchema.shape,
        ...searchParamsPaginationSchema.shape,
        ...searchParamsSortingSchema(BusinessSortBy, BusinessSortBy.UNSPECIFIED)
            .shape,
        ...searchParamsSearchTextSchema.shape,
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsDeletedItemSelectionDefaultValues,
                ...searchParamsPaginationDefaultValues,
                ...searchParamsSortingDefaultValues(BusinessSortBy.UNSPECIFIED),
                ...searchParamsSearchTextDefaultValues,
            }),
        ],
    },

    beforeLoad: ({
        search: {
            deletedItemSelection,
            page,
            pageSize,
            sortByField,
            sortDirectionDesc,
            searchText,
        },
        context: { queryClient, grpcClients, user },
    }) => {
        assertUserIsAdminOrRedirect(user)

        return queryClient.ensureQueryData(
            businessesQueryOptions(
                grpcClients,
                businessQueryFilter(
                    deletedItemSelection,
                    pageSize,
                    page,
                    sortByField,
                    sortDirectionDesc,
                    searchText
                )
            )
        )
    },
    pendingMs: 2000,
    pendingComponent: PendingComponent,
})
